import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, LoaderCircle, PlayCircle, Star } from "lucide-react";
import Swal from "sweetalert2";

import { extractErrorMessage } from "../../../../../api";
import {
    fetchLearnerEvaluationCurrentApi,
    fetchLearnerEvaluationHistoryApi,
    startLearnerEvaluationApi,
    submitLearnerEvaluationApi,
} from "../../../../services/moduleEvaluationApi";
import EvaluationQuestionRenderer from "./EvaluationQuestionRenderer";
import EvaluationSubmitSummary from "./EvaluationSubmitSummary";
import "./QuizRunner.css";
import "./EvaluationRunner.css";

const isAnswered = (question, value) => {
    if (!question?.supportedInV1) return !question?.required;
    if (question.questionType === "BOOLEAN") return value === true || value === false;
    if (question.questionType === "RATING") return value !== null && value !== undefined && value !== "";
    if (question.questionType === "CHOICE") return value !== null && value !== undefined && value !== "";
    return String(value || "").trim().length > 0;
};

const createInitialAnswers = (sections = []) =>
    sections.reduce((accumulator, section) => {
        (section.questions || []).forEach((question) => {
            if (question.questionType === "RATING") {
                accumulator[question.id] = question.submittedRatingValue ?? "";
            } else if (question.questionType === "BOOLEAN") {
                accumulator[question.id] = question.submittedBooleanValue ?? "";
            } else if (question.questionType === "TEXT") {
                accumulator[question.id] = question.submittedTextAnswer || "";
            } else if (question.questionType === "CHOICE") {
                accumulator[question.id] = question.submittedChoiceOptionId ?? "";
            }
        });
        return accumulator;
    }, {});

const buildAnswerPayload = (question, value) => {
    const base = { questionId: question.id, questionType: question.questionType };
    if (question.questionType === "RATING") return { ...base, ratingValue: Number(value) };
    if (question.questionType === "BOOLEAN") return { ...base, booleanValue: value };
    if (question.questionType === "TEXT") return { ...base, textAnswer: String(value || "") };
    if (question.questionType === "CHOICE") return { ...base, choiceOptionId: Number(value) };
    return base;
};

export default function EvaluationRunner({ moduleId, content, onRefreshPlayer }) {
    const [currentData, setCurrentData] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestSeqRef = useRef(0);
    const currentContentIdRef = useRef(content?.id);

    const contentId = content?.id;
    currentContentIdRef.current = contentId;
    const questions = useMemo(
        () => (currentData?.sections || []).flatMap((section) => section.questions || []),
        [currentData?.sections]
    );
    const answeredCount = questions.filter((question) => isAnswered(question, answers[question.id])).length;

    const refreshData = useCallback(async () => {
        if (!moduleId || !contentId) return;
        const requestSeq = ++requestSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const [currentResponse, historyResponse] = await Promise.all([
                fetchLearnerEvaluationCurrentApi(moduleId, contentId),
                fetchLearnerEvaluationHistoryApi(moduleId, contentId),
            ]);
            if (requestSeq !== requestSeqRef.current) return;
            setCurrentData(currentResponse.data);
            setHistoryData(historyResponse.data);
            setAnswers(createInitialAnswers(currentResponse.data?.sections || []));
        } catch (requestError) {
            if (requestSeq !== requestSeqRef.current) return;
            setError(extractErrorMessage(requestError, "Impossible de charger l'evaluation apprenant."));
        } finally {
            if (requestSeq === requestSeqRef.current) {
                setLoading(false);
            }
        }
    }, [contentId, moduleId]);

    useEffect(() => {
        requestSeqRef.current += 1;
        setCurrentData(null);
        setHistoryData(null);
        setAnswers({});
        setResult(null);
        setError(null);
        setActionLoading(false);
        setLoading(true);
        refreshData();
    }, [contentId, moduleId, refreshData]);

    const handleStart = async () => {
        const startContentId = contentId;
        setActionLoading(true);
        setError(null);
        try {
            const response = await startLearnerEvaluationApi(moduleId, contentId);
            if (String(currentContentIdRef.current) !== String(startContentId)) return;
            const payload = response.data?.evaluation;
            setCurrentData(payload);
            setAnswers(createInitialAnswers(payload?.sections || []));
            setResult(null);
            await refreshData();
        } catch (requestError) {
            if (String(currentContentIdRef.current) !== String(startContentId)) return;
            setError(extractErrorMessage(requestError, "Impossible de demarrer l'evaluation."));
        } finally {
            if (String(currentContentIdRef.current) === String(startContentId)) {
                setActionLoading(false);
            }
        }
    };

    const handleAnswerChange = (questionId, value) => {
        setAnswers((previous) => ({
            ...previous,
            [questionId]: value,
        }));
    };

    const handleSubmit = async () => {
        const submitContentId = contentId;
        if (!currentData?.attemptId) {
            setError("Impossible de soumettre l'evaluation : tentative introuvable.");
            return;
        }
        const blockingUnsupportedQuestions = questions.filter((question) => question.required && !question.supportedInV1);
        if (blockingUnsupportedQuestions.length) {
            setError("Une question obligatoire n'est pas encore supportee.");
            return;
        }
        const missingQuestions = questions.filter((question) => question.required && !isAnswered(question, answers[question.id]));
        if (missingQuestions.length) {
            setError("Merci de renseigner toutes les questions obligatoires avant la soumission.");
            return;
        }

        setActionLoading(true);
        setError(null);
        try {
            const supportedAnswers = questions
                .filter((question) => question.supportedInV1)
                .filter((question) => isAnswered(question, answers[question.id]))
                .map((question) => buildAnswerPayload(question, answers[question.id]));

            const response = await submitLearnerEvaluationApi(moduleId, submitContentId, {
                attemptId: currentData.attemptId,
                answers: supportedAnswers,
            });
            if (String(currentContentIdRef.current) !== String(submitContentId)) return;
            setResult(response.data);
            await refreshData();
            if (typeof onRefreshPlayer === "function") {
                onRefreshPlayer();
            }
            await Swal.fire({
                icon: "success",
                text: response.data.message,
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (requestError) {
            if (String(currentContentIdRef.current) !== String(submitContentId)) return;
            setError(extractErrorMessage(requestError, "Impossible de soumettre l'evaluation."));
        } finally {
            if (String(currentContentIdRef.current) === String(submitContentId)) {
                setActionLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="qr-state">
                <LoaderCircle className="qr-spin" size={18} />
                Chargement de l'evaluation...
            </div>
        );
    }

    if (error) {
        return <div className="qr-state qr-state--error">{error}</div>;
    }

    const activeAttempt = currentData?.attemptStatus === "IN_PROGRESS";
    const latestAttempt = historyData?.attempts?.[0];
    const alreadySubmitted = ["SUBMITTED", "PASSED", "FAILED", "NEEDS_REVIEW"].includes(String(latestAttempt?.status || currentData?.attemptStatus || "").toUpperCase());
    const canStart = Boolean(currentData?.canStart) && !alreadySubmitted;

    return (
        <div className="qr-shell er-shell">
            <div className="qr-header">
                <div>
                    <span className="qr-eyebrow">Evaluation ModuleCle</span>
                    <h3>{content.evaluationLabel || currentData?.evaluationName || "Evaluation"}</h3>
                    <p>{currentData?.evaluationDescription || content.description || "Completez cette evaluation depuis le player apprenant."}</p>
                </div>
                <div className="qr-stats">
                    <span><Star size={14} /> Moyenne: {latestAttempt?.scoreApplicable ? latestAttempt.averageScore : "--"}</span>
                    <span><CheckCircle2 size={14} /> Statut: {latestAttempt?.status || "A demarrer"}</span>
                </div>
            </div>

            {result ? <EvaluationSubmitSummary result={result} /> : null}

            {!result && alreadySubmitted ? (
                <EvaluationSubmitSummary
                    result={{
                        message: latestAttempt?.scoreApplicable
                            ? `Evaluation deja soumise. Moyenne: ${latestAttempt.averageScore}.`
                            : "Evaluation deja soumise. Les reponses non notees ont ete enregistrees.",
                        scoreApplicable: latestAttempt?.scoreApplicable,
                        averageScore: latestAttempt?.averageScore,
                        status: latestAttempt?.status || currentData?.attemptStatus,
                    }}
                />
            ) : null}

            {activeAttempt ? (
                <div className="qr-attempt-card">
                    <div className="qr-attempt-header">
                        <strong>Évaluation en cours</strong>
                        <p>{answeredCount} / {questions.length} question(s) renseignée(s)</p>
                    </div>

                    <div className="qr-questions">
                        {(currentData?.sections || []).map((section) => (
                            <section key={section.id} className="er-section">
                                <div className="er-section-head">
                                    <h4>{section.title}</h4>
                                    <span>Poids {section.weight ?? 0}%</span>
                                </div>
                                {(section.questions || []).map((question) => (
                                    <article key={question.id} className="qr-question-card">
                                        <div className="qr-question-head">
                                            <span>Question {question.position}{question.required ? " - obligatoire" : ""}</span>
                                            <strong>{question.label}</strong>
                                        </div>
                                        <EvaluationQuestionRenderer
                                            question={question}
                                            scaleOptions={currentData?.scaleOptions || []}
                                            value={answers[question.id]}
                                            onChange={(value) => handleAnswerChange(question.id, value)}
                                            disabled={actionLoading}
                                        />
                                    </article>
                                ))}
                            </section>
                        ))}
                    </div>

                    <div className="qr-inline-actions">
                        <button className="cr-btn cr-btn--primary" onClick={handleSubmit} disabled={actionLoading}>
                            {actionLoading ? "Soumission..." : "Soumettre l'évaluation"}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="qr-start-card">
                    <div className="qr-start-state">
                        <PlayCircle size={22} />
                        <div>
                            <strong>{alreadySubmitted ? "Évaluation déjà soumise" : "Prêt à lancer l'évaluation"}</strong>
                            <p>{alreadySubmitted ? "Vous pouvez consulter l'historique de votre soumission." : "Vous pouvez commencer cette évaluation."}</p>
                        </div>
                    </div>
                    {canStart ? (
                        <div className="qr-inline-actions">
                            <button className="cr-btn cr-btn--primary" onClick={handleStart} disabled={actionLoading}>
                                Commencer l'évaluation
                            </button>
                        </div>
                    ) : null}
                </div>
            )}

            <div className="qr-history-card">
                <h4>Historique des évaluations</h4>
                {(historyData?.attempts || []).length ? (
                    <div className="qr-history-list">
                        {historyData.attempts.map((attempt) => {
                            const status = String(attempt.status || "").toUpperCase();
                            const isSuccess = status === "PASSED" || status === "SUBMITTED";
                            const isFailed = status === "FAILED";
                            const isInProgress = status === "IN_PROGRESS";

                            return (
                                <div key={attempt.attemptId} className="qr-history-item">
                                    <div>
                                        <strong>Évaluation #{attempt.attemptId}</strong>
                                        <p>Date: {attempt.dateTentative || "--"}</p>
                                    </div>
                                    <div>
                                        <span className={`qr-badge ${isSuccess ? "qr-badge--success" : isFailed ? "qr-badge--failed" : isInProgress ? "qr-badge--progress" : "qr-badge--neutral"}`}>
                                            {attempt.status || "Inconnu"}
                                        </span>
                                    </div>
                                    <div>
                                        <strong>{attempt.scoreApplicable ? `${attempt.averageScore}%` : "--"}</strong>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="qr-empty">Aucune évaluation soumise pour le moment.</p>
                )}
            </div>
        </div>
    );
}
